import type { Axios } from "axios";

import type {
  BackendFilters,
  DataGridFilter,
  FilterLogicalOperator,
} from "../../utils/backendFilters";

export interface BankTransactionSearchParams {
  practiceIds: string[];
  tab: string;
  page: number;
  pageSize: number;
  sortField: string | null;
  sortDirection: "asc" | "desc";
  filters: BackendFilters;
  devMode: boolean;
}

export interface FindMatchesResponse {
  autoMatched: boolean;
  paymentKey: string | null;
  payerPaymentId: string | null;
  suggestedMatches: Array<{
    paymentKey: string;
    payerPaymentId?: string;
    paymentAmount?: number;
    paymentDate?: string;
    payer?: string;
  }>;
}

export const searchBankTransactions = (
  c: Axios,
  params: BankTransactionSearchParams,
  signal?: AbortSignal,
) => {
  const requestBody: {
    filters: Record<string, (string | null)[]>;
    page: number;
    page_size: number;
    sort: string[];
    sort_direction: string;
    tab: string;
    datagrid_filters?: DataGridFilter[];
    logical_operator?: FilterLogicalOperator;
    devmode: boolean;
  } = {
    filters: params.practiceIds.length
      ? { practice_id: params.practiceIds }
      : {},
    page: params.page,
    page_size: params.pageSize,
    sort: [params.sortField ?? "bank_date"],
    sort_direction: params.sortDirection,
    tab: params.tab,
    devmode: params.devMode,
  };

  if (params.filters.datagridFilters?.length) {
    requestBody.datagrid_filters = params.filters.datagridFilters;
  }
  if (params.filters.logicalOperator) {
    requestBody.logical_operator = params.filters.logicalOperator;
  }

  return c
    .post<SearchResponse>("/bank-transactions/search", requestBody, { signal })
    .then((r) => r.data);
};

export const getBankTransactionById = (
  c: Axios,
  wieldyId: string,
  signal?: AbortSignal,
) =>
  c
    .get<BankTransactionMessage>(
      `/bank-transactions/${wieldyId}${window.location.search}`,
      { signal },
    )
    .then((r) => r.data);

export const updateBankTransaction = (
  c: Axios,
  update: BankTransactionUpdateMessage,
) =>
  c
    .patch<BankTransactionMessage>(
      `/bank-transactions/${update.wieldyId}${window.location.search}`,
      update,
    )
    .then((r) => r.data);

export const unmatchBankTransaction = (c: Axios, wieldyId: string) =>
  c
    .post<BankTransactionMessage>(
      `/bank-transactions/${wieldyId}/unmatch${window.location.search}`,
    )
    .then((r) => r.data);

export const uploadBankTransactions = (
  c: Axios,
  practiceId: string,
  file: File,
) => {
  const formData = new FormData();
  formData.append("file", file);
  return c
    .post<CsvUploadResponseMessage>(
      `/bank-transactions/upload/${practiceId}${window.location.search}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((r) => r.data);
};

export const reconcileBankTransactions = (
  c: Axios,
  practiceId: string,
  transactions: BankTransactionMessage[],
  csvFileId?: string,
) =>
  c
    .post<BankTransactionUploadMessage>(
      `/bank-transactions/reconcile/${practiceId}${window.location.search}`,
      { transactions, csvFileId },
    )
    .then((r) => r.data);

export const findMatchesForTransaction = (c: Axios, wieldyId: string) =>
  c
    .post(
      `/bank-transactions/${wieldyId}/find-matches${window.location.search}`,
    )
    .then(
      (r): FindMatchesResponse => ({
        autoMatched: r.data.autoMatched,
        paymentKey: r.data.paymentKey,
        payerPaymentId: r.data.payerPaymentId,
        suggestedMatches: r.data.suggestedMatches || [],
      }),
    );
